import React, { type ComponentProps } from 'react';
import { Button, Flex, Box } from '@skin-seoul/design-system';

type ButtonProps = ComponentProps<typeof Button>;
type FlexProps = ComponentProps<typeof Flex>;
type BoxProps = ComponentProps<typeof Box>;

export const SelectButton = React.forwardRef<
  HTMLButtonElement,
  {
    children: React.ReactNode;
    onClick: () => void;
  } & Omit<ButtonProps, 'children' | 'onClick'>
>(({ children, onClick, ...props }, ref) => (
  <Button
    ref={ref}
    onClick={onClick}
    w="full"
    display="inline-flex"
    alignItems="center"
    justifyContent="space-between"
    h="36px"
    px="12px"
    bg="generic.0"
    border="1px solid"
    borderColor="neutral.100"
    borderRadius="8px"
    color="neutral.700"
    cursor="pointer"
    whiteSpace="nowrap"
    gap={{ base: '0', tablet: '32px' }}
    textStyle="paragraph.small"
    _hover={{ bg: 'neutral.50' }}
    _focus={{ outline: 'none', borderColor: 'primary.400' }}
    css={{
      '& span': {
        fontSize: '14px',
        color: 'neutral.700',
        flexShrink: 0,
      },
      '&[as="select"]': {
        appearance: 'none',
        paddingRight: '30px',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 6L0.535898 0L7.4641 0L4 6Z' fill='%23737373'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 16px center',
        '& option': {
          color: '#2e2e2e',
          fontSize: '14px',
        },
        '& option:first-of-type': {
          color: '#737373',
          fontSize: '12px',
        },
      },
    }}
    {...props}
  >
    {children}
  </Button>
));

SelectButton.displayName = 'SelectButton';

export const MultipleOptionList = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
  } & Omit<FlexProps, 'children'>
>(({ children, ...props }, ref) => (
  <Flex ref={ref} direction="column" w="full" minW="240px" gap={{ base: '16px', tablet: '0' }} {...props}>
    {children}
  </Flex>
));

MultipleOptionList.displayName = 'MultipleOptionList';

export const MultipleOptionCheckbox = ({
  checked,
  children,
  ...props
}: {
  checked: boolean;
  children?: React.ReactNode;
} & Omit<BoxProps, 'children'>) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    w="24px"
    h="24px"
    border={checked ? 'none' : '2px solid'}
    borderColor="neutral.100"
    borderRadius="8px"
    bg={checked ? 'primary.400' : 'generic.0'}
    flexShrink="0"
    transition="border-color 0.15s, background 0.15s"
    boxSizing="border-box"
    minW="24px"
    minH="24px"
    css={{
      '& svg': {
        color: '#fff',
        width: '20px',
        height: '20px',
      },
    }}
    {...props}
  >
    {children}
  </Box>
);

export const MultipleOptionLabel = ({
  children,
  ...props
}: {
  children: React.ReactNode;
} & Omit<BoxProps, 'children'>) => (
  <Box
    as="span"
    flex="1"
    style={{ fontSize: '14px' }}
    color="#737373"
    fontFamily="Plus Jakarta Sans, sans-serif"
    textAlign="left"
    fontWeight="400"
    {...props}
  >
    {children}
  </Box>
);

export const MultipleOptionItem = ({
  children,
  isSelected: _isSelected,
  onClick,
  ...props
}: {
  children: React.ReactNode;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
} & Omit<FlexProps, 'children' | 'onClick'>) => (
  <Flex
    align="center"
    justify="space-between"
    w="full"
    textAlign="left"
    cursor="pointer"
    style={{ fontSize: '15px' }}
    color="#2e2e2e"
    fontFamily="Plus Jakarta Sans, sans-serif"
    transition="background 0.15s, color 0.15s"
    boxSizing="border-box"
    _hover={{ bg: '#f5f5f7' }}
    onClick={onClick}
    {...props}
  >
    {children}
  </Flex>
);
